import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, CheckCircle2, Calendar, Clock, User, AlertTriangle,
  Send, Filter, Copy, X, Undo2,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Button, Pill, Spinner, EmptyState, KPI,
  Textarea,
} from "@/components/ui";
import { fmtMoney } from "@/lib/dates";
import { format, parseISO } from "date-fns";

// =============================================================================
// TimesheetApprovalsScreen — /hr/timesheet-approvals
// Manager/director queue for submitted timesheet weeks awaiting approval.
// Complements the existing per-cell approvals on TimesheetsScreen — this one
// is the dedicated inbox view for "what does the crew need signed off?".
// Approve-week calls app.approve_timesheet_week RPC (migration 038), which
// stamps approved_at on every unapproved entry the employee owns in that
// period and notifies them via the notifications outbox. T-008 Commit C.
// =============================================================================

interface QueueEntry {
  entry_id:       string;
  period_id:      string;
  employee_id:    string;
  work_date:      string;
  total_hours:    number;
  hours_th:       number;
  hours_dt:       number;
  is_night:       boolean;         // mig 350 — night works: whole entry at DT
  lafha_amount:   number | null;
  lafha_nights:   number | null;   // T-324 — carry nights so an edit can't null them + approver can verify
  travel_km:      number | null;
  travel_hours:   number | null;
  submitted_at:   string | null;
  approved_at:    string | null;
  employees?:     { first_name: string | null; last_name: string | null } | null;
  timesheet_periods?: { period_start: string; period_end: string; status: string } | null;
}

interface GroupedWeek {
  period_id:      string;
  employee_id:    string;
  employee_name:  string;
  period_start:   string;
  period_end:     string;
  status:         string;
  entry_count:    number;
  total_hours:    number;
  ot_hours:       number;
  night_hours:    number;          // mig 350
  lafha_amount:   number;
  lafha_nights:   number;   // T-324
  travel_km:      number;
  travel_hours:   number;
  earliest_submit: string | null;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "";
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

type TabKey = "pending" | "recent" | "all";

// -----------------------------------------------------------------------------

export default function TimesheetApprovalsScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canApprove = hasRole(user, "manager");
  const canMirror = hasRole(user, "director");   // T-365 — mirroring is director-only
  const [tab, setTab] = useState<TabKey>("pending");
  const [acting, setActing] = useState<string | null>(null); // keyed by period_id|employee_id
  const [sendBack, setSendBack] = useState<{ period_id: string; employee_id: string; employee_name: string; period_start: string; period_end: string } | null>(null); // mig 383
  const [mirrorFrom, setMirrorFrom] = useState<GroupedWeek | null>(null); // T-365 mirror source week

  // -- Load the submitted-but-unapproved backlog --------------------------
  // We read entries directly rather than the summary view so we can tell the
  // difference between submitted-and-pending and untouched drafts. Keep the
  // row count down by filtering to submitted_at not null.
  const { data: pendingRows = [], isLoading: loadingPending } = useQuery({
    queryKey: ["timesheet_approvals_pending"],
    enabled: canApprove,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheet_entries")
        .select(`
          entry_id, period_id, employee_id, work_date, total_hours,
          hours_th, hours_dt, is_night, lafha_amount, lafha_nights, travel_km, travel_hours,
          submitted_at, approved_at,
          employees:employee_id(first_name, last_name),
          timesheet_periods(period_start, period_end, status)
        `)
        .not("submitted_at", "is", null)
        .is("approved_at", null)
        .order("submitted_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as QueueEntry[];
    },
  });

  // -- Recently approved (last 14 days) -----------------------------------
  const fourteenDaysAgo = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 14);
    return d.toISOString();
  }, []);

  const { data: recentRows = [], isLoading: loadingRecent } = useQuery({
    queryKey: ["timesheet_approvals_recent"],
    enabled: canApprove && tab === "recent",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheet_entries")
        .select(`
          entry_id, period_id, employee_id, work_date, total_hours,
          hours_th, hours_dt, is_night, lafha_amount, lafha_nights, travel_km, travel_hours,
          submitted_at, approved_at,
          employees:employee_id(first_name, last_name),
          timesheet_periods(period_start, period_end, status)
        `)
        .not("approved_at", "is", null)
        .gte("approved_at", fourteenDaysAgo)
        .order("approved_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as QueueEntry[];
    },
  });

  // -- All approved (no date window) — fortnightly cycles mean the last
  //    approval is often older than 14 days, so "Recently approved" reads empty.
  //    This tab shows every approved week so you can find one to mirror. --------
  const { data: allApprovedRows = [], isLoading: loadingAll } = useQuery({
    queryKey: ["timesheet_approvals_all"],
    enabled: canApprove && tab === "all",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheet_entries")
        .select(`
          entry_id, period_id, employee_id, work_date, total_hours,
          hours_th, hours_dt, is_night, lafha_amount, lafha_nights, travel_km, travel_hours,
          submitted_at, approved_at,
          employees:employee_id(first_name, last_name),
          timesheet_periods(period_start, period_end, status)
        `)
        .not("approved_at", "is", null)
        .order("approved_at", { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as unknown as QueueEntry[];
    },
  });

  // -- Group by (period + employee) ---------------------------------------
  function groupEntries(rows: QueueEntry[]): GroupedWeek[] {
    const map = new Map<string, GroupedWeek>();
    for (const r of rows) {
      const key = `${r.period_id}|${r.employee_id}`;
      const g = map.get(key);
      const name =
        [r.employees?.first_name, r.employees?.last_name].filter(Boolean).join(" ") ||
        "Unknown";
      const ot = (r.hours_th ?? 0) + (r.hours_dt ?? 0);
      const nightH = r.is_night ? Number(r.total_hours ?? 0) : 0;
      const lafha = Number(r.lafha_amount ?? 0);
      const nights = Number(r.lafha_nights ?? 0);
      const tkm = Number(r.travel_km ?? 0);
      const thrs = Number(r.travel_hours ?? 0);
      if (!g) {
        map.set(key, {
          period_id:     r.period_id,
          employee_id:   r.employee_id,
          employee_name: name,
          period_start:  r.timesheet_periods?.period_start ?? "",
          period_end:    r.timesheet_periods?.period_end ?? "",
          status:        r.timesheet_periods?.status ?? "",
          entry_count:   1,
          total_hours:   r.total_hours,
          ot_hours:      ot,
          night_hours:   nightH,
          lafha_amount:  lafha,
          lafha_nights:  nights,
          travel_km:     tkm,
          travel_hours:  thrs,
          earliest_submit: r.submitted_at,
        });
      } else {
        g.entry_count   += 1;
        g.total_hours   += r.total_hours;
        g.ot_hours      += ot;
        g.night_hours   += nightH;
        g.lafha_amount  += lafha;
        g.lafha_nights  += nights;
        g.travel_km     += tkm;
        g.travel_hours  += thrs;
        if (r.submitted_at && (!g.earliest_submit || r.submitted_at < g.earliest_submit)) {
          g.earliest_submit = r.submitted_at;
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      // oldest-submitted first so stale weeks rise to the top
      if (a.earliest_submit && b.earliest_submit) {
        return a.earliest_submit.localeCompare(b.earliest_submit);
      }
      return a.period_start.localeCompare(b.period_start);
    });
  }

  const pendingGroups = useMemo(() => groupEntries(pendingRows), [pendingRows]);
  const recentGroups  = useMemo(() => groupEntries(recentRows),  [recentRows]);
  const allGroups     = useMemo(() => groupEntries(allApprovedRows), [allApprovedRows]);

  // -- KPI roll-ups -------------------------------------------------------
  const kpis = useMemo(() => ({
    people: new Set(pendingGroups.map(g => g.employee_id)).size,
    weeks:  pendingGroups.length,
    hours:  pendingGroups.reduce((a, g) => a + g.total_hours, 0),
    ot:     pendingGroups.reduce((a, g) => a + g.ot_hours, 0),
    lafha:  pendingGroups.reduce((a, g) => a + g.lafha_amount, 0),
    nights: pendingGroups.reduce((a, g) => a + g.lafha_nights, 0),
  }), [pendingGroups]);

  // -- Approve week mutation ---------------------------------------------
  const approveWeek = useMutation({
    mutationFn: async (args: { period_id: string; employee_id: string }) => {
      setActing(`${args.period_id}|${args.employee_id}`);
      const { data, error } = await supabase
        .schema("app")
        .rpc("approve_timesheet_week", {
          p_period_id:   args.period_id,
          p_employee_id: args.employee_id,
        });
      if (error) throw error;
      return data as number;
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: ["timesheet_approvals_pending"] });
      qc.invalidateQueries({ queryKey: ["timesheet_approvals_recent"] });
      qc.invalidateQueries({ queryKey: ["timesheet_summary"] });
      qc.invalidateQueries({ queryKey: ["timesheet_entries"] });
      if (count === 0) alert("Nothing to approve — all entries already approved.");
    },
    onError: (e: any) => alert(e?.message ?? "Approve failed"),
    onSettled: () => setActing(null),
  });

  if (!user) return null;

  if (!canApprove) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card>
          <div className="p-10 text-center">
            <AlertTriangle size={32} className="mx-auto text-warn mb-3" />
            <div className="text-text font-medium">Access denied</div>
            <div className="text-text-muted text-[13px] mt-1">
              Only directors and managers can approve timesheets.
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const groups = tab === "pending" ? pendingGroups : tab === "recent" ? recentGroups : allGroups;
  const loading = tab === "pending" ? loadingPending : tab === "recent" ? loadingRecent : loadingAll;

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <button
        onClick={() => navigate("/hr")}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> HR hub
      </button>

      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Timesheet approvals</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Weeks submitted by the crew, waiting for your sign-off. Oldest first.
          </div>
        </div>
        {/* T-305: removed "Open weekly view" button. After T-276 made
            /timesheets personal-only for every role, this link dumped the
            director into their own personal week instead of a multi-employee
            view. HR is self-contained — approve from the summary, no link
            back to Personal. */}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-5">
        <KPI label="Pending weeks"  value={kpis.weeks} />
        <KPI label="Employees"      value={kpis.people} />
        <KPI label="Hours pending"  value={kpis.hours.toFixed(1)} />
        <KPI label="Overtime"       value={`${kpis.ot.toFixed(1)}h`} sub="TH + DT" accent={kpis.ot > 40} />
        <KPI label="LAFHA pending"  value={fmtMoney(kpis.lafha)} sub={`${kpis.nights} night${kpis.nights === 1 ? "" : "s"}`} accent={kpis.nights > 0} />
      </div>

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md w-fit">
        {([
          { key: "pending", label: `Pending${pendingGroups.length ? ` · ${pendingGroups.length}` : ""}` },
          { key: "recent",  label: "Recently approved" },
          { key: "all",     label: "All approved" },
        ] as const).map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded text-[12px] transition-colors ${
              tab === t.key
                ? "bg-accent/15 text-accent border border-accent/30"
                : "text-text-muted hover:text-text"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="py-20 flex justify-center"><Spinner size={20} /></div>
      )}

      {!loading && groups.length === 0 && (
        <Card>
          <EmptyState
            title={tab === "pending" ? "Nothing to approve" : tab === "all" ? "No approved weeks yet" : "No recent approvals"}
            description={
              tab === "pending"
                ? "The crew's up to date. Nothing submitted that you haven't signed off."
                : tab === "all"
                  ? "No timesheet weeks have been approved yet. Approve a submitted week first, then it can be mirrored."
                  : "Nothing approved in the last 14 days — try the All approved tab."
            }
            icon={tab === "pending" ? CheckCircle2 : Filter}
          />
        </Card>
      )}

      {!loading && groups.length > 0 && (
        <div className="space-y-3">
          {groups.map(g => {
            const key = `${g.period_id}|${g.employee_id}`;
            const isActing = acting === key;
            const pending = tab === "pending";
            return (
              <Card key={key} className="hover:border-line-light transition-colors">
                <div className="p-4 md:p-5 flex items-center gap-4 flex-wrap">
                  <div className="w-10 h-10 rounded-md bg-accent/10 text-accent flex items-center justify-center border border-accent/30 flex-shrink-0">
                    <User size={18} />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-text font-medium">{g.employee_name}</div>
                    <div className="text-[12px] text-text-muted mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <Calendar size={11} />
                      {g.period_start && (
                        <>
                          {format(parseISO(g.period_start), "dd MMM")} – {format(parseISO(g.period_end), "dd MMM yyyy")}
                        </>
                      )}
                      {g.earliest_submit && pending && (
                        <span className="text-text-dim">· submitted {timeAgo(g.earliest_submit)}</span>
                      )}
                    </div>
                  </div>
                  {/* Fixed, right-aligned columns — every row renders all five in
                      the same slots (— when empty) so they line up down the page. */}
                  <div className="flex items-start gap-4 md:gap-5 text-[12px] font-mono flex-shrink-0">
                    <div className="w-10 text-right">
                      <div className="text-[10px] uppercase tracking-wider2 text-text-muted">Entries</div>
                      <div className="text-text">{g.entry_count}</div>
                    </div>
                    <div className="w-14 text-right">
                      <div className="text-[10px] uppercase tracking-wider2 text-text-muted">Hours</div>
                      <div className="text-text">{g.total_hours.toFixed(1)}</div>
                    </div>
                    <div className="w-14 text-right">
                      <div className="text-[10px] uppercase tracking-wider2 text-text-muted">OT</div>
                      <div className={g.ot_hours > 0 ? "text-warn" : "text-text-dim"}>{g.ot_hours > 0 ? `${g.ot_hours.toFixed(1)}h` : "—"}</div>
                    </div>
                    <div className="w-14 text-right" title="Night works — every hour at double time (mig 350)">
                      <div className="text-[10px] uppercase tracking-wider2 text-text-muted">Night</div>
                      <div className={g.night_hours > 0 ? "text-accent" : "text-text-dim"}>{g.night_hours > 0 ? `${g.night_hours.toFixed(1)}h` : "—"}</div>
                    </div>
                    <div className="w-28 text-right">
                      <div className="text-[10px] uppercase tracking-wider2 text-text-muted">LAFHA</div>
                      <div className={g.lafha_amount > 0 || g.lafha_nights > 0 ? "text-text" : "text-text-dim"}>{g.lafha_amount > 0 || g.lafha_nights > 0 ? `${fmtMoney(g.lafha_amount)}${g.lafha_nights > 0 ? ` · ${g.lafha_nights}n` : ""}` : "—"}</div>
                    </div>
                    <div className="w-24 text-right">
                      <div className="text-[10px] uppercase tracking-wider2 text-text-muted">Travel</div>
                      <div className={g.travel_km > 0 || g.travel_hours > 0 ? "text-text" : "text-text-dim"}>{g.travel_km > 0 || g.travel_hours > 0 ? `${g.travel_km > 0 ? `${g.travel_km.toFixed(0)}km` : ""}${g.travel_km > 0 && g.travel_hours > 0 ? " · " : ""}${g.travel_hours > 0 ? `${g.travel_hours.toFixed(1)}h` : ""}` : "—"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* T-311: "View week" now points to /hr/team-week (the
                        HR-side read-only grid), not the old /timesheets link
                        that T-305 had to remove because /timesheets is
                        personal-only after T-276. */}
                    <Link to={`/hr/team-week?week=${g.period_start}`}>
                      <Button variant="secondary" size="sm" icon={Clock}>View week</Button>
                    </Link>
                    {pending ? (
                      <>
                      {/* mig 383 — James: "I will NOT be held stuck on a Monday night …
                          I need to edit and they need to be able to edit if I send it back."
                          Edit = View week (manager quick-add / remove). Send back = every
                          submitted entry of the week returns to draft with the reason, the
                          employee is notified and can fix + resubmit. */}
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={Undo2}
                        disabled={isActing}
                        onClick={() => setSendBack({ period_id: g.period_id, employee_id: g.employee_id, employee_name: g.employee_name, period_start: g.period_start, period_end: g.period_end })}
                        title="Send the week back to the employee to fix"
                      >
                        Send back
                      </Button>
                      <Button
                        size="sm"
                        icon={CheckCircle2}
                        disabled={isActing}
                        onClick={() => {
                          if (!window.confirm(
                            `Approve ${g.employee_name}'s week starting ${g.period_start}?\n\n${g.entry_count} entr${g.entry_count === 1 ? "y" : "ies"} · ${g.total_hours.toFixed(1)}h total${g.ot_hours > 0 ? ` (${g.ot_hours.toFixed(1)}h OT)` : ""}.\n\nThey'll be notified the week is approved.`
                          )) return;
                          approveWeek.mutate({ period_id: g.period_id, employee_id: g.employee_id });
                        }}
                      >
                        {isActing ? "Approving…" : "Approve week"}
                      </Button>
                      </>
                    ) : (
                      <>
                        <Pill label="approved" tone="approved" small />
                        {/* T-365 — mirror this approved week onto a crew lead
                            (e.g. Allan) who doesn't self-timesheet. Director-only. */}
                        {canMirror && (
                          <Button variant="secondary" size="sm" icon={Copy}
                            onClick={() => setMirrorFrom(g)}
                            title="Copy this approved week onto another employee (e.g. the crew lead)">
                            Mirror
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Footer helper */}
      <div className="mt-6 text-[11px] text-text-muted">
        Approve-week notifies the employee. To fix hours yourself, open View week (add or remove a day's entry). To have the employee fix it, Send back — the week returns to draft with your note and they resubmit.
      </div>

      {sendBack && (
        <SendBackModal
          target={sendBack}
          onClose={() => setSendBack(null)}
          onDone={(count) => {
            setSendBack(null);
            qc.invalidateQueries({ queryKey: ["timesheet_approvals_pending"] });
            qc.invalidateQueries({ queryKey: ["timesheet_approvals_recent"] });
            qc.invalidateQueries({ queryKey: ["timesheet_summary"] });
            qc.invalidateQueries({ queryKey: ["timesheet_entries"] });
            alert(`Sent back ${count} entr${count === 1 ? "y" : "ies"}. ${sendBack.employee_name} has been notified.`);
          }}
        />
      )}

      {mirrorFrom && (
        <MirrorWeekModal
          source={mirrorFrom}
          onClose={() => setMirrorFrom(null)}
          onDone={(count, name) => {
            setMirrorFrom(null);
            qc.invalidateQueries({ queryKey: ["timesheet_approvals_recent"] });
            qc.invalidateQueries({ queryKey: ["timesheet_summary"] });
            qc.invalidateQueries({ queryKey: ["timesheet_entries"] });
            alert(`Mirrored ${count} day${count === 1 ? "" : "s"} onto ${name}. It's approved and now in project cost + payroll.`);
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// MirrorWeekModal — T-365
// Clone an approved crew-mate's week onto another employee (a crew lead who
// doesn't self-timesheet, e.g. Allan). Calls app.mirror_timesheet_week.
// =============================================================================
function MirrorWeekModal({
  source,
  onClose,
  onDone,
}: {
  source: GroupedWeek;
  onClose: () => void;
  onDone: (count: number, targetName: string) => void;
}) {
  const [targetId, setTargetId] = useState("");
  // T-473 — defaults to OFF so a mirrored week matches the week it came from.
  // Flattening stays available (this is the crew-lead tool, e.g. Allan, whose
  // pay is settled outside the award) but must now be a deliberate tick.
  const [noOt, setNoOt] = useState(false);

  const { data: people = [] } = useQuery({
    queryKey: ["mirror_target_employees"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, is_active")
        .eq("is_active", true)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const targets = useMemo(
    () => people.filter((p) => p.employee_id !== source.employee_id),
    [people, source.employee_id],
  );
  const targetName = useMemo(() => {
    const t = people.find((p) => p.employee_id === targetId);
    return t ? [t.first_name, t.last_name].filter(Boolean).join(" ") : "";
  }, [people, targetId]);

  const run = useMutation({
    mutationFn: async () => {
      if (!targetId) throw new Error("Pick who to mirror onto");
      const { data, error } = await supabase.schema("app").rpc("mirror_timesheet_week", {
        p_period_id:          source.period_id,
        p_source_employee_id: source.employee_id,
        p_target_employee_id: targetId,
        p_suppress_ot:        noOt,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: (count) => onDone(count, targetName || "employee"),
    onError: (e: any) => alert(e?.message ?? "Mirror failed"),
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-line flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Mirror week onto another person</h3>
            <div className="text-[12px] text-text-muted mt-1">
              Copies <span className="text-text">{source.employee_name}</span>'s approved week
              {source.period_start ? ` (${format(parseISO(source.period_start), "dd MMM")})` : ""} —
              same days, projects and hours — onto someone who was with the crew but doesn't do their own timesheet.
            </div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-wider2 text-text-muted mb-1">Mirror onto</label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text"
            >
              <option value="">— pick a person —</option>
              {targets.map((p) => (
                <option key={p.employee_id} value={p.employee_id}>
                  {[p.first_name, p.last_name].filter(Boolean).join(" ")}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={noOt} onChange={(e) => setNoOt(e.target.checked)} className="accent-accent mt-0.5" />
            <span className="text-[13px] text-text">
              Book as ordinary hours only (no overtime)
              <span className="block text-[11px] text-text-muted mt-0.5">
                Leave this OFF and the copy books the award, exactly like the week it came from — the two weeks will match in payroll.
                Tick it only for a crew lead whose pay is settled outside the award (e.g. a flat fortnight): every hour is then booked
                as ordinary, so the payroll export shows no penalty hours and will NOT match the source week. Project cost is the same either way.
              </span>
            </span>
          </label>

          <div className="text-[11px] text-text-muted bg-ink-raised border border-line rounded px-3 py-2">
            Only fills days the person has <span className="text-text">no timesheet on</span> — any day they've already entered is left untouched. The mirrored week is auto-approved, so it lands in project cost and the payroll export straight away. Re-mirroring refreshes the copy.
          </div>
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button icon={Copy} disabled={run.isPending || !targetId} onClick={() => run.mutate()}>
            {run.isPending ? "Mirroring…" : "Mirror week"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// SendBackModal — mig 383. Reason is required; app.return_timesheet_week puts
// every submitted-not-approved entry of the week back to draft with the reason
// in rejection_notes and notifies the employee. Leave days are never returned.
// -----------------------------------------------------------------------------
function SendBackModal({ target, onClose, onDone }: {
  target: { period_id: string; employee_id: string; employee_name: string; period_start: string; period_end: string };
  onClose: () => void; onDone: (count: number) => void;
}) {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: async () => {
      if (!reason.trim()) throw new Error("Tell them what to fix.");
      const { data, error } = await supabase.schema("app").rpc("return_timesheet_week" as any, {
        p_period_id: target.period_id, p_employee_id: target.employee_id, p_reason: reason.trim(),
      } as any);
      if (error) throw error;
      return Number(data ?? 0);
    },
    onSuccess: (count) => onDone(count),
    onError: (e: any) => setErr(e?.message ?? e?.details ?? "Send back failed"),
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md" onClick={e => e.stopPropagation()}>
        <Card>
          <div className="p-5 border-b border-line flex items-center justify-between">
            <div className="text-[14px] font-medium text-text">Send {target.employee_name}'s week back</div>
            <button onClick={onClose} className="text-text-muted hover:text-text" type="button"><X size={18} /></button>
          </div>
          <div className="p-5 space-y-3">
            <div className="text-[12px] text-text-muted">
              Week {target.period_start} → {target.period_end}. Every submitted day goes back to draft with your note; they fix it and submit again. Approved leave days stay as they are.
            </div>
            <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Thursday should be 8h not 10h — check your finish time" autoFocus />
            {err && <div className="text-[12px] text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">{err}</div>}
          </div>
          <div className="p-4 border-t border-line flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            <Button icon={Undo2} onClick={() => mut.mutate()} disabled={!reason.trim() || mut.isPending} type="button">
              {mut.isPending ? "Sending…" : "Send back"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
